import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Search, RefreshCw, Phone, Mail, FileText, X } from "lucide-react";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["All", "New", "Contacted", "In Progress", "Completed"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [fileBlob, setFileBlob] = useState(null);

  useEffect(() => {
    const saved = sessionStorage.getItem("admin_pwd");
    if (!saved) { navigate("/admin", { replace: true }); return; }
    setPassword(saved);
  }, [navigate]);

  const load = useCallback(async (pwd, q, st) => {
    if (!pwd) return;
    setLoading(true);
    try {
      const client = adminApi(pwd);
      const [{ data: list }, { data: s }] = await Promise.all([
        client.get("/admin/tickets", { params: { q: q || undefined, status: st || undefined } }),
        client.get("/admin/stats"),
      ]);
      setTickets(list);
      setStats(s);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem("admin_pwd");
        navigate("/admin", { replace: true });
      } else {
        toast.error("Failed to load tickets");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (password) load(password, search, statusFilter);
  }, [password, statusFilter, load]); // eslint-disable-line

  const onSearch = (e) => {
    e.preventDefault();
    load(password, search, statusFilter);
  };

  const updateStatus = async (id, status) => {
    try {
      await adminApi(password).patch(`/admin/tickets/${id}`, { status });
      toast.success("Status updated");
      load(password, search, statusFilter);
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch {
      toast.error("Failed to update");
    }
  };

  const openTicket = async (t) => {
    setSelected(t);
    setFileBlob(null);
    if (t.has_file) {
      try {
        const { data } = await adminApi(password).get(`/admin/tickets/${t.id}/file`);
        setFileBlob(data);
      } catch { /* ignore */ }
    }
  };

  const logout = () => {
    sessionStorage.removeItem("admin_pwd");
    navigate("/admin", { replace: true });
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-slate-50">
      <header className="bg-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold">Admin Dashboard</h1>
            <p className="text-sm text-slate-300">Alberta Traffic Tickets Defense</p>
          </div>
          <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-md">
            <LogOut className="w-4 h-4" /> Log Out
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <Stat label="Total" value={stats.total} testid="stat-total" />
            <Stat label="New" value={stats.new} testid="stat-new" accent />
            <Stat label="Contacted" value={stats.contacted} testid="stat-contacted" />
            <Stat label="In Progress" value={stats.in_progress} testid="stat-inprogress" />
            <Stat label="Completed" value={stats.completed} testid="stat-completed" />
          </div>
        )}

        {/* Controls */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 flex flex-col md:flex-row gap-3">
          <form onSubmit={onSearch} className="flex-1 flex gap-2">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                data-testid="admin-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, phone, email, city, ticket type..."
                className="w-full h-11 pl-10 pr-4 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-navy"
              />
            </div>
            <button type="submit" className="bg-navy text-white px-4 rounded-md font-semibold hover:bg-slate-800">Search</button>
          </form>
          <div className="flex gap-2">
            <select
              data-testid="admin-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-11 px-3 border border-slate-300 rounded-md bg-white"
            >
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <button onClick={() => load(password, search, statusFilter)} className="h-11 px-4 border border-slate-300 rounded-md bg-white hover:bg-slate-50 inline-flex items-center gap-2">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </div>
        </div>

        {/* Tickets table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-base">
              <thead className="bg-slate-100 text-left">
                <tr>
                  <Th>Name</Th><Th>Contact</Th><Th>City</Th><Th>Type</Th><Th>Submitted</Th><Th>Status</Th><Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="p-8 text-center text-gray-600">Loading...</td></tr>
                ) : tickets.length === 0 ? (
                  <tr><td colSpan={7} className="p-8 text-center text-gray-600">No tickets found.</td></tr>
                ) : tickets.map((t) => (
                  <tr key={t.id} data-testid={`ticket-row-${t.id}`} className="border-t border-slate-200 hover:bg-slate-50">
                    <Td><div className="font-semibold text-navy">{t.full_name}</div></Td>
                    <Td>
                      <div className="text-sm">{t.phone}</div>
                      {t.email && <div className="text-sm text-gray-600">{t.email}</div>}
                    </Td>
                    <Td>{t.city}</Td>
                    <Td>{t.ticket_type}</Td>
                    <Td className="whitespace-nowrap text-sm text-gray-600">{new Date(t.created_at).toLocaleString()}</Td>
                    <Td>
                      <select
                        data-testid={`ticket-status-${t.id}`}
                        value={t.status}
                        onChange={(e) => updateStatus(t.id, e.target.value)}
                        className="h-9 px-2 border border-slate-300 rounded-md bg-white text-sm"
                      >
                        {STATUSES.filter(s => s !== "All").map(s => <option key={s}>{s}</option>)}
                      </select>
                    </Td>
                    <Td>
                      <button onClick={() => openTicket(t)} data-testid={`ticket-view-${t.id}`} className="text-navy font-semibold hover:text-gold">View</button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/50" onClick={() => setSelected(null)} />
          <div data-testid="ticket-drawer" className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl">
            <div className="bg-navy text-white px-6 py-4 flex justify-between items-center">
              <div>
                <h2 className="font-heading text-xl font-bold">{selected.full_name}</h2>
                <p className="text-sm text-slate-300">{selected.ticket_type}</p>
              </div>
              <button onClick={() => setSelected(null)} className="p-2 hover:bg-white/10 rounded-md"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-5">
              <Row label="Phone"><a href={`tel:${selected.phone}`} className="text-navy font-semibold inline-flex items-center gap-1"><Phone className="w-4 h-4" /> {selected.phone}</a></Row>
              {selected.email && <Row label="Email"><a href={`mailto:${selected.email}`} className="text-navy font-semibold inline-flex items-center gap-1"><Mail className="w-4 h-4" /> {selected.email}</a></Row>}
              <Row label="City">{selected.city}</Row>
              {selected.court_date && <Row label="Court Date">{selected.court_date}</Row>}
              <Row label="Status">{selected.status}</Row>
              <Row label="Submitted">{new Date(selected.created_at).toLocaleString()}</Row>
              {selected.additional_info && (
                <Row label="Additional Info"><span className="whitespace-pre-wrap">{selected.additional_info}</span></Row>
              )}

              {selected.analysis && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <h3 className="font-heading text-lg font-semibold text-navy mb-2">AI Summary</h3>
                  <div className="space-y-1 text-sm text-gray-800">
                    {selected.analysis.ticket_type && <div><b>Type:</b> {selected.analysis.ticket_type}</div>}
                    {selected.analysis.fine_amount && <div><b>Fine:</b> {selected.analysis.fine_amount}</div>}
                    {selected.analysis.court_date && <div><b>Court Date:</b> {selected.analysis.court_date}</div>}
                    {selected.analysis.offence_date && <div><b>Offence Date:</b> {selected.analysis.offence_date}</div>}
                    {selected.analysis.location && <div><b>Location:</b> {selected.analysis.location}</div>}
                    {selected.analysis.demerit_points && <div><b>Demerit Points:</b> {selected.analysis.demerit_points}</div>}
                    {selected.analysis.court_required && <div><b>Court Required:</b> {selected.analysis.court_required}</div>}
                  </div>
                  <p data-testid="drawer-ai-summary" className="mt-3 text-base text-gray-900">{selected.analysis.summary}</p>
                </div>
              )}

              {selected.has_file && fileBlob && (
                <div>
                  <h3 className="font-heading text-lg font-semibold text-navy mb-2 flex items-center gap-2"><FileText className="w-5 h-5" /> Uploaded Ticket</h3>
                  {fileBlob.file_mime?.startsWith("image/") ? (
                    <img alt="Ticket" src={`data:${fileBlob.file_mime};base64,${fileBlob.file_base64}`} className="w-full rounded-md border border-slate-200" />
                  ) : (
                    <a
                      href={`data:${fileBlob.file_mime};base64,${fileBlob.file_base64}`}
                      download={fileBlob.file_name || "ticket"}
                      className="btn-secondary"
                    >
                      Download {fileBlob.file_name || "file"}
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Th({ children }) { return <th className="px-4 py-3 font-semibold text-navy">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 align-top ${className}`}>{children}</td>; }
function Stat({ label, value, accent, testid }) {
  return (
    <div data-testid={testid} className={`rounded-lg p-4 border ${accent ? "bg-gold text-black border-gold" : "bg-white border-slate-200"}`}>
      <div className="text-sm font-semibold uppercase tracking-wider">{label}</div>
      <div className="font-heading text-3xl font-bold mt-1">{value}</div>
    </div>
  );
}
function Row({ label, children }) {
  return (
    <div>
      <div className="text-sm font-semibold uppercase tracking-wider text-gray-600">{label}</div>
      <div className="text-lg text-gray-900 mt-0.5">{children}</div>
    </div>
  );
}
